import { useState } from "react";
import { 
  Clock,
  Calendar,
  CalendarDays,
  CalendarRange,
  Check,
  ChevronDown,
  ChevronUp,
  MapPin,
  MessageCircle,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const pathways = [
  {
    id: "first-24-hours",
    title: "First 24 Hours",
    icon: Clock,
    accentColor: "bg-rose-100",
    iconColor: "text-rose-600",
    borderColor: "border-rose-200",
    checkColor: "bg-rose-500",
    encouragement: "You made it. Focus on the basics today—everything else can wait.",
    tasks: [
      "Find a safe place to sleep tonight",
      "Get a meal",
      "Contact a family member or someone you trust",
      "Check in with your probation or parole officer if required",
      "Write down important phone numbers"
    ]
  },
  {
    id: "first-week",
    title: "First Week",
    icon: Calendar,
    accentColor: "bg-amber-100",
    iconColor: "text-amber-600",
    borderColor: "border-amber-200",
    checkColor: "bg-amber-500",
    encouragement: "One day at a time. This week is about laying the groundwork.",
    tasks: [
      "Secure stable or transitional housing",
      "Apply for a Minnesota state ID at a DMV office",
      "Apply for food assistance (called SNAP or EBT)",
      "Enroll in health coverage through Medical Assistance",
      "Get a free phone through the Lifeline program"
    ]
  },
  {
    id: "first-month",
    title: "First Month",
    icon: CalendarDays,
    accentColor: "bg-sky-100",
    iconColor: "text-sky-600",
    borderColor: "border-sky-200",
    checkColor: "bg-sky-500",
    encouragement: "You're building momentum. Every small step counts.",
    tasks: [
      "Start a job search or enroll in a training program",
      "Schedule an appointment with a doctor or clinic",
      "Set up bus or transit access with a reduced-fare card",
      "Attend a recovery meeting or peer support group",
      "Open a checking or savings account at a bank"
    ]
  },
  {
    id: "three-to-six-months",
    title: "3-6 Months",
    icon: CalendarRange,
    accentColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
    borderColor: "border-emerald-200",
    checkColor: "bg-emerald-500",
    encouragement: "Look how far you've come. Now you can plan for what's next.",
    tasks: [
      "Move into permanent housing",
      "Advance in your current job or find a better position",
      "File a petition to clear eligible offenses from your record",
      "Register to vote at your local county office",
      "Set up automatic savings, even a small amount each month"
    ]
  }
];

const Pathways = () => {
  const [completedTasks, setCompletedTasks] = useState({});
  const [expandedSections, setExpandedSections] = useState({ "first-24-hours": true });
  const [currentStage, setCurrentStage] = useState("first-24-hours");

  const toggleTask = (pathwayId, taskIndex) => {
    const key = `${pathwayId}-${taskIndex}`;
    setCompletedTasks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSection = (pathwayId) => {
    setExpandedSections(prev => ({ ...prev, [pathwayId]: !prev[pathwayId] }));
    setCurrentStage(pathwayId);
  };

  const getCompletedCount = (pathwayId, tasks) => {
    return tasks.filter((_, idx) => completedTasks[`${pathwayId}-${idx}`]).length;
  };

  const currentPathway = pathways.find(p => p.id === currentStage);

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="pathways-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-slate-600" />
            </div>
            <h1 className="text-xl font-semibold text-slate-800">Reentry Pathways</h1>
          </div>
          <p className="text-slate-500 text-sm leading-relaxed">
            A simple guide to help you move forward, one step at a time.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        {/* Intro Card */}
        <div className="mb-6 p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed">
              <span className="font-medium text-slate-700">Everyone's journey is different.</span>{" "}
              Use this as a guide, not a rulebook. Check off what you've done, skip what doesn't apply.
            </p>
          </div>
        </div>

        {/* Pathway Sections */}
        <div className="space-y-4">
          {pathways.map((pathway, index) => {
            const isExpanded = expandedSections[pathway.id];
            const isCurrent = currentStage === pathway.id;
            const completedCount = getCompletedCount(pathway.id, pathway.tasks);
            const isComplete = completedCount === pathway.tasks.length;
            const isPast = pathways.findIndex(p => p.id === currentStage) > index;
            
            return (
              <div 
                key={pathway.id}
                className={`bg-white rounded-2xl border-2 shadow-sm transition-all duration-200 ${
                  isCurrent 
                    ? `${pathway.borderColor} shadow-md` 
                    : "border-slate-200"
                } ${!isCurrent && !isPast ? "opacity-70" : ""}`}
                data-testid={`pathway-${pathway.id}`}
              >
                {/* Accordion Header */}
                <button
                  onClick={() => toggleSection(pathway.id)}
                  className="w-full p-4 sm:p-5 flex items-center gap-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-2xl"
                  aria-expanded={isExpanded}
                >
                  {/* Icon */}
                  <div className={`w-12 h-12 ${pathway.accentColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <pathway.icon className={`w-6 h-6 ${pathway.iconColor}`} />
                  </div>
                  
                  {/* Title & Progress */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {isCurrent && (
                        <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                          Start here
                        </span>
                      )}
                      {isComplete && (
                        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                          Complete
                        </span>
                      )}
                    </div>
                    <h2 className="font-semibold text-slate-800 text-base sm:text-lg">
                      {pathway.title}
                    </h2>
                    <p className="text-slate-500 text-sm mt-0.5">
                      {completedCount} of {pathway.tasks.length} completed
                    </p>
                  </div>
                  
                  {/* Progress Ring & Chevron */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Mini Progress */}
                    <div className="relative w-10 h-10">
                      <svg className="w-10 h-10 transform -rotate-90">
                        <circle
                          cx="20"
                          cy="20"
                          r="16"
                          stroke="currentColor"
                          strokeWidth="3"
                          fill="none"
                          className="text-slate-200"
                        />
                        <circle
                          cx="20"
                          cy="20"
                          r="16"
                          stroke="currentColor"
                          strokeWidth="3"
                          fill="none"
                          strokeDasharray={`${(completedCount / pathway.tasks.length) * 100.5} 100.5`}
                          className={pathway.iconColor}
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-slate-600">
                        {completedCount}/{pathway.tasks.length}
                      </span>
                    </div>
                    
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Accordion Content */}
                {isExpanded && (
                  <div className="px-4 sm:px-5 pb-5 pt-1 animate-fade-in">
                    {/* Encouragement */}
                    <p className="text-slate-500 text-sm italic mb-4 pl-1 border-l-2 border-slate-200 ml-1">
                      {pathway.encouragement}
                    </p>
                    
                    {/* Task List */}
                    <div className="space-y-2">
                      {pathway.tasks.map((task, idx) => {
                        const isCompleted = completedTasks[`${pathway.id}-${idx}`];
                        return (
                          <button
                            key={idx}
                            onClick={() => toggleTask(pathway.id, idx)}
                            className={`w-full flex items-start gap-4 text-left p-4 rounded-xl transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              isCompleted 
                                ? "bg-slate-50" 
                                : "bg-slate-50 hover:bg-slate-100 active:bg-slate-200"
                            }`}
                            aria-pressed={isCompleted}
                          >
                            {/* Large Checkbox */}
                            <div 
                              className={`w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center transition-all duration-150 ${
                                isCompleted 
                                  ? `${pathway.checkColor} shadow-sm` 
                                  : "border-2 border-slate-300 bg-white hover:border-slate-400"
                              }`}
                            >
                              {isCompleted && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                            </div>
                            
                            {/* Task Text */}
                            <span 
                              className={`text-base leading-relaxed ${
                                isCompleted 
                                  ? "text-slate-400 line-through" 
                                  : "text-slate-700"
                              }`}
                            >
                              {task}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Closing Message */}
        <div className="mt-6 p-5 bg-white border border-slate-200 rounded-2xl text-center shadow-sm">
          <p className="text-slate-700 font-medium mb-1">Progress isn't always a straight line.</p>
          <p className="text-slate-500 text-sm">Some days will be harder than others. That's okay.</p>
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex gap-3">
          <Link to="/resources" className="flex-1">
            <Button 
              variant="outline"
              className="w-full h-12 text-slate-700 border-slate-300 hover:bg-slate-50 font-medium"
              data-testid="find-resources-btn"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Find Resources
            </Button>
          </Link>
          <Button 
            onClick={() => document.querySelector('[data-testid="chatbot-toggle"]')?.click()}
            className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium"
            data-testid="chat-help-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Chat for Help
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Pathways;
